/* eslint-disable no-nested-ternary */
import { useEffect, useState } from 'react';
import { Button as OldButton } from '@components/Button';
import {
  Modal,
  ModalContainer,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from '@components/Modal';
import {
  MdOutlineAddCircle,
  MdLocationOn,
  MdEditNote,
  MdLocalShipping,
  MdAdd,
  MdStore,
} from 'react-icons/md';
import { HiOutlineOfficeBuilding } from 'react-icons/hi';
import { FloxSpinner } from '@tailwindComponents/FloxSpinner';
import { useAuth } from '@context';
import { UserRoleService } from '@services';

import { useDocumentTitle, useMediaQuery } from 'usehooks-ts';
import { WarehouseCard } from '@components/WarehouseList';
import { useAtom } from 'jotai';
import { useNavigate } from 'react-router-dom';
import { Warehouse } from '@views/WarehouseDetails/WarehouseDetails';
import { AgreeToWarehouseTandCsModal } from '@components/AgreeToWarehouseTandCsModal';
import { Depot } from '../DepotDetails/DepotDetails';
import { PageHeader } from '../../tailwindComponents/PageHeader';
import { Button } from '../../tailwindComponents/Button';
import { businessProfileAtom, notificationsAtom } from '../../store/jotai';
import {
  LSP_WAREHOUSES_URL,
  DEPOT_LIST,
  DEPOT_IMAGES_URL,
} from '../../constants';
import { useAuthHttp } from '../../hooks';

const depotImagePlaceholder = `/images/warehouse_details_placeholder.png`;

function DepotCard({ depot, onClick }: { depot: Depot; onClick: () => void }) {
  const fleetCount =
    depot.fleet?.reduce((sum, v) => sum + (Number(v.count) || 0), 0) || 0;

  const hasActualImage = depot.images && depot.images.length > 0;
  const imageUrl = hasActualImage
    ? `${DEPOT_IMAGES_URL}/${depot.images![0].imageLocation}/small/${
        depot.images![0].remoteName
      }`
    : depotImagePlaceholder;

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events
    <div
      className='group relative flex flex-col bg-white border border-gray-200 rounded-lg overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-gray-300 w-[220px]'
      onClick={onClick}
    >
      {/* Draft Badge */}
      {depot.isDraft && (
        <div className='absolute top-2 right-2 z-10 flex items-center gap-1 bg-gray-100 text-gray-600 text-xs font-medium px-2 py-1 rounded-full'>
          <MdEditNote className='w-3.5 h-3.5' />
          <span>Draft</span>
        </div>
      )}

      {/* Image container */}
      <div className='relative h-[140px] bg-gray-50'>
        <img
          src={imageUrl}
          alt={depot.name}
          className={`w-full h-full ${
            hasActualImage ? 'object-cover' : 'object-contain p-4'
          } ${depot.isDraft ? 'opacity-60' : ''}`}
        />
      </div>

      {/* Info section */}
      <div className='flex flex-col gap-1.5 p-3 border-t border-gray-100'>
        <h3 className='text-base font-semibold text-gray-900 truncate'>
          {depot.name}
        </h3>
        {depot.address && (
          <div className='flex items-center gap-1.5 text-sm text-gray-500'>
            <MdLocationOn className='w-4 h-4 flex-shrink-0' />
            <span className='truncate'>{depot.address}</span>
          </div>
        )}
        {fleetCount > 0 && (
          <div className='flex items-center gap-1.5 text-sm text-gray-500'>
            <MdLocalShipping className='w-4 h-4 flex-shrink-0' />
            <span>
              {fleetCount} vehicle{fleetCount !== 1 ? 's' : ''}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export function WarehouseMaster() {
  const { user } = useAuth();
  const { isWarehouseClient } = UserRoleService();

  const [businessProfile] = useAtom(businessProfileAtom);
  const [notifications] = useAtom(notificationsAtom);

  const [isWarehousesLoading, setIsWarehousesLoading] = useState(true);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [isAgreeToWarehouseTandCsOpen, setIsAgreeToWarehouseTandCsOpen] =
    useState(false);

  const [isNoBusinessProfileOpen, setIsNoBusinessProfileOpen] = useState(false);

  const [isDepotsLoading, setIsDepotsLoading] = useState(true);
  const [depots, setDepots] = useState<Depot[]>([]);

  const authHttp = useAuthHttp();
  const navigate = useNavigate();

  const isDesktop = useMediaQuery('(min-width: 62em)');

  useDocumentTitle('FLOX - Warehouses');

  const fetchWarehouses = async () => {
    try {
      const { data } = await authHttp.post(LSP_WAREHOUSES_URL, {
        pagination: {
          itemsPerPage: isDesktop ? 100 : 25,
        },
        isIncludingDelistedWarehouses: null,
      });

      if (data) setWarehouses(data.warehouses);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsWarehousesLoading(false);
    }
  };

  const fetchDepots = async () => {
    try {
      const { data } = await authHttp.post(DEPOT_LIST, {
        pagination: {
          itemsPerPage: 100,
        },
      });

      if (data) setDepots(data.depots);
    } catch (error) {
      console.error(error);
    } finally {
      setIsDepotsLoading(false);
    }
  };

  const handleAfterAgreeingToWarehouseTandCs = async () => {
    navigate('/warehouse/create');
  };

  const handleGoToBusinessProfile = () => {
    setIsAgreeToWarehouseTandCsOpen(false);
    navigate('/profile');
  };

  useEffect(() => {
    fetchWarehouses();
    fetchDepots();
  }, []);

  useEffect(() => {
    const notification = notifications?.[0];
    if (
      notification &&
      [
        'Warehouse.Created',
        'Warehouse.Updated',
        'Warehouse.Deleted',
        'Warehouse.Delisted',
      ].includes(notification.notificationType)
    ) {
      fetchWarehouses();
    }
    if (
      notification &&
      [
        'Depot.Created',
        'Depot.Updated',
        'Depot.Deleted',
        'Depot.Delisted',
      ].includes(notification.notificationType)
    ) {
      fetchDepots();
    }
  }, [notifications]);

  const handleAddWarehouse = () => {
    if (!businessProfile) {
      setIsNoBusinessProfileOpen(true);
    } else if (
      !businessProfile.warehouseTandCAgreement?.hasAgreed &&
      businessProfile
    ) {
      setIsAgreeToWarehouseTandCsOpen(true);
    } else {
      navigate('/warehouse/create');
    }
  };

  const handleStartNewWarehouseSearch = () => {
    navigate('/warehouse-search');
  };

  const warehouseActionButton = isWarehouseClient(user) ? (
    <Button
      label='New Search'
      intent='primary'
      startIcon={<HiOutlineOfficeBuilding />}
      onClick={handleStartNewWarehouseSearch}
    />
  ) : (
    <Button
      label='Add Warehouse'
      intent='primary'
      startIcon={<MdOutlineAddCircle />}
      onClick={handleAddWarehouse}
    />
  );

  const pages = [
    { name: 'Home', href: '/', current: false },
    { name: 'Warehouses', href: '/warehouses', current: true },
  ];

  const isLoading = isWarehousesLoading && isDepotsLoading;

  return (
    <>
      <PageHeader header='Warehouses' pages={pages}>
        {warehouseActionButton}
      </PageHeader>
      <main className='flex flex-col gap-4 min-h-[calc(100vh-4rem)] lg:min-h-[calc(100vh-5rem)] p-4 lg:pt-4 lg:px-8 lg:pb-8 relative'>
        {/* Loading State */}
        {isLoading && !warehouses.length && !depots.length && (
          <div className='flex items-center justify-center h-[calc(100vh-12rem)]'>
            <div className='flex flex-col items-center gap-3'>
              <FloxSpinner size='md' />
              <p className='text-gray-500 text-sm'>Loading...</p>
            </div>
          </div>
        )}

        {/* Empty State — no warehouses and no depots */}
        {!isWarehousesLoading &&
          !warehouses.length &&
          !isDepotsLoading &&
          !depots.length && (
            <div className='flex flex-col items-center justify-center h-[calc(100vh-12rem)]'>
              <div className='flex flex-col items-center text-center max-w-md'>
                <div className='mb-6'>
                  <svg
                    className='w-16 h-16 text-gray-300'
                    viewBox='0 0 64 64'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <rect
                      x='8'
                      y='20'
                      width='48'
                      height='36'
                      rx='2'
                      stroke='currentColor'
                      strokeWidth='2'
                      fill='none'
                    />
                    <path d='M8 28h48' stroke='currentColor' strokeWidth='2' />
                    <path
                      d='M32 8L8 20M32 8L56 20'
                      stroke='currentColor'
                      strokeWidth='2'
                      strokeLinecap='round'
                    />
                    <rect
                      x='16'
                      y='34'
                      width='8'
                      height='10'
                      stroke='currentColor'
                      strokeWidth='2'
                      fill='none'
                    />
                    <rect
                      x='28'
                      y='34'
                      width='8'
                      height='10'
                      stroke='currentColor'
                      strokeWidth='2'
                      fill='none'
                    />
                    <rect
                      x='40'
                      y='34'
                      width='8'
                      height='10'
                      stroke='currentColor'
                      strokeWidth='2'
                      fill='none'
                    />
                    <path
                      d='M26 48h12v8H26z'
                      stroke='currentColor'
                      strokeWidth='2'
                      fill='none'
                    />
                  </svg>
                </div>
                <h2 className='text-xl font-semibold text-gray-900 mb-2'>
                  No warehouses yet.
                </h2>
                <p className='text-gray-500 text-sm mb-6'>
                  Add your first warehouse to start managing your storage
                  locations and services.
                </p>
                {warehouseActionButton}
              </div>
            </div>
          )}

        {/* Content — at least one warehouse or depot exists */}
        {(!isWarehousesLoading || !isDepotsLoading) &&
          (warehouses.length > 0 || depots.length > 0) && (
            <>
              {/* Warehouse List */}
              {warehouses.length > 0 && (
                <>
                  <div className='flex items-center gap-2 mb-4'>
                    <MdStore className='w-5 h-5 text-gray-700' />
                    <h2 className='text-lg font-semibold text-gray-900'>
                      Warehouses
                    </h2>
                  </div>
                  <div className='flex flex-wrap items-start gap-4'>
                    {warehouses
                      .filter((warehouse) => !warehouse.isDelisted)
                      .map((warehouse, index) => (
                        <WarehouseCard
                          {...(index === 0 && { id: 'first' })}
                          key={warehouse.warehouseId}
                          warehouseSummary={warehouse}
                          orientation='horizontal'
                          onClick={() =>
                            navigate(`/warehouse/${warehouse.warehouseId}`)
                          }
                        />
                      ))}
                  </div>

                  {/* Delisted Warehouses */}
                  {warehouses.some((w) => w.isDelisted) && (
                    <>
                      <hr className='border-gray-200' />
                      <h2 className='text-lg font-semibold text-gray-900'>
                        Delisted Warehouses
                      </h2>
                      <div className='flex flex-wrap items-start gap-4'>
                        {warehouses
                          .filter((warehouse) => warehouse.isDelisted)
                          .map((warehouse, index) => (
                            <WarehouseCard
                              {...(index === 0 && { id: 'first' })}
                              key={warehouse.warehouseId}
                              warehouseSummary={warehouse}
                              orientation='horizontal'
                              onClick={() =>
                                navigate(`/warehouse/${warehouse.warehouseId}`)
                              }
                            />
                          ))}
                      </div>
                    </>
                  )}
                </>
              )}

              {/* Depots Section */}
              <div className='mt-8 pt-6 border-t-2 border-gray-300'>
                <div className='flex items-start justify-between mb-4 lg:px-2'>
                  <h2 className='text-3xl font-bold text-gray-950 font-inter'>
                    Depots
                  </h2>
                  <Button
                    label='Add Depot'
                    intent='primary'
                    startIcon={<MdOutlineAddCircle />}
                    onClick={() => navigate('/depot/create')}
                  />
                </div>

                {isDepotsLoading && !depots.length && (
                  <div className='flex items-center justify-center py-12'>
                    <div className='flex flex-col items-center gap-3'>
                      <FloxSpinner size='sm' />
                      <p className='text-gray-500 text-sm'>Loading depots...</p>
                    </div>
                  </div>
                )}

                {!isDepotsLoading && !depots.length && (
                  <div className='flex flex-col items-center py-12 text-center'>
                    <p className='text-gray-500 text-sm'>
                      No depots yet. Depots are automatically created when you
                      add fleet to a warehouse, or you can add one manually.
                    </p>
                  </div>
                )}

                {!isDepotsLoading && depots.length > 0 && (
                  <>
                    <div className='flex flex-wrap items-start gap-4'>
                      {depots
                        .filter((depot) => !depot.isDelisted)
                        .map((depot) => (
                          <DepotCard
                            key={depot.depotId}
                            depot={depot}
                            onClick={() => navigate(`/depot/${depot.depotId}`)}
                          />
                        ))}
                    </div>

                    {/* Delisted Depots */}
                    {depots.some((d) => d.isDelisted) && (
                      <>
                        <hr className='border-gray-200 mt-4 mb-4' />
                        <h3 className='text-base font-semibold text-gray-900 mb-4'>
                          Delisted Depots
                        </h3>
                        <div className='flex flex-wrap items-start gap-4'>
                          {depots
                            .filter((depot) => depot.isDelisted)
                            .map((depot) => (
                              <DepotCard
                                key={depot.depotId}
                                depot={depot}
                                onClick={() =>
                                  navigate(`/depot/${depot.depotId}`)
                                }
                              />
                            ))}
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>
            </>
          )}

        {/* Agree to T&Cs Modal */}
        <AgreeToWarehouseTandCsModal
          isAgreeToWarehouseTandCsOpen={isAgreeToWarehouseTandCsOpen}
          setIsAgreeToWarehouseTandCsOpen={setIsAgreeToWarehouseTandCsOpen}
          handleAfterAgreeingToWarehouseTandCs={
            handleAfterAgreeingToWarehouseTandCs
          }
        />

        {/* No Business Profile Modal */}
        <Modal
          open={isNoBusinessProfileOpen}
          onOpenChange={(isOpen: boolean) => {
            setIsNoBusinessProfileOpen(isOpen);
          }}
        >
          <ModalContainer preventCloseOnOutsideClick>
            <ModalHeader title='Action Required: Finalise Your Corporate Details' />
            <ModalContent>
              <p className='text-gray-700'>
                To agree to the Warehouse T&Cs and complete your first
                warehouse, we need you to finalise your corporate details.
                Please update your Business Profile by clicking the button
                below.
              </p>
            </ModalContent>
            <ModalFooter>
              <OldButton variant='primary' onClick={handleGoToBusinessProfile}>
                Take me to my Business Profile
              </OldButton>
            </ModalFooter>
          </ModalContainer>
        </Modal>
      </main>
    </>
  );
}
